import { WORD_LENGTH, type GuessEntry, type SpectateDraft } from "@mk/shared";
import { messages } from "../i18n/index.ts";

interface ResultBoardProps {
  rows: readonly GuessEntry[];
  /**
   * Tahtayı bu satır sayısına kadar boş kutucuklarla doldurur.
   *
   * İzleme ekranında gerekiyor: hayalet tahta altı satırı da çizdiği için,
   * yanındaki harfli tahta yalnızca yapılmış tahminleri gösterince iki taraf
   * farklı boyda duruyor ve "kaç hak kaldı" görünmüyordu. Sonuç ekranlarında
   * verilmiyor — orada maç bitmiş, boş satır bilgi taşımıyor.
   */
  padTo?: number;
  /** İzlerken henüz gönderilmemiş, renksiz aktif satır. */
  draft?: SpectateDraft | null;
  /** Sunucunun bu oyuncu için izin verdiği toplam tahmin sayısı. */
  attemptLimit?: number;
}

/**
 * Harf görme yetkisi olan izleyicinin ya da maç sonunun küçük sonuç tahtası.
 */
export function ResultBoard({ rows, padTo, draft, attemptLimit = padTo }: ResultBoardProps) {
  const visibleDraft =
    draft?.row === rows.length &&
    draft.row < (attemptLimit ?? Number.POSITIVE_INFINITY) &&
    draft.cells.some((cell) => cell !== null)
      ? draft
      : null;
  const empty = Math.max(0, (padTo ?? 0) - rows.length - (visibleDraft ? 1 : 0));
  const emptyStart = rows.length + (visibleDraft ? 1 : 0);
  if (rows.length === 0 && !visibleDraft && empty === 0) {
    return <p className="duel-result-board__empty">{messages.board.noGuesses}</p>;
  }

  return (
    <div className="duel-result-board" aria-label={messages.board.guesses}>
      {rows.map((row, r) => (
        <div key={r} className="duel-result-board__row">
          {[...row.word].map((letter, c) => (
            <span key={c} className="duel-result-board__tile" data-state={row.pattern[c]}>
              {letter}
            </span>
          ))}
        </div>
      ))}

      {visibleDraft && (
        <div className="duel-result-board__row" aria-label={messages.board.draft}>
          {visibleDraft.cells.map((letter, c) => (
            <span
              key={c}
              className="duel-result-board__tile duel-result-board__tile--draft"
            >
              {letter ?? ""}
            </span>
          ))}
        </div>
      )}

      {Array.from({ length: empty }, (_, r) => (
        <div
          key={`bos-${r}`}
          className="duel-result-board__row"
          aria-label={
            emptyStart + r >= (attemptLimit ?? Number.POSITIVE_INFINITY)
              ? messages.board.locked
              : undefined
          }
          aria-hidden={
            emptyStart + r < (attemptLimit ?? Number.POSITIVE_INFINITY) ? "true" : undefined
          }
        >
          {Array.from({ length: WORD_LENGTH }, (_, c) => {
            const locked = emptyStart + r >= (attemptLimit ?? Number.POSITIVE_INFINITY);
            return (
              <span
                key={c}
                className={`duel-result-board__tile ${
                  locked
                    ? "duel-result-board__tile--locked"
                    : "duel-result-board__tile--empty"
                }`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
